import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Search from "@mui/icons-material/Search";
import './ProfilePage.css';
import api from '../lib/api';  // <-- use shared API client (baseURL: '/api')

export default function ProfilePage() {
    const navigate = useNavigate();
    const fileInputRef = useRef(null);
    const [me, setMe] = useState({ id: null, email: '' });
    const [profile, setProfile] = useState({ username: '', gender: '', dob: '' });
    const [pfp, setPfp] = useState('');
    const [query, setQuery] = useState('');
    const [pwd, setPwd] = useState({ current: '', next: '', confirm: '' });
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const meRes = await api.get('/me');
                if (!alive) return;
                setMe(meRes.data);

                const profRes = await api.get('/profile');
                if (!alive) return;
                setProfile({
                    username: profRes.data.username || '',
                    gender: profRes.data.gender || '',
                    dob: profRes.data.dob || ''
                });
                setPfp(profRes.data.pfp || '');
            } catch { }
        })();
        return () => { alive = false; };
    }, []);

    const changePassword = async (e) => {
        e.preventDefault();
        if (!pwd.current || !pwd.next || !pwd.confirm) return alert('Please fill out all password fields.');
        if (pwd.next.length < 6) return alert('New password must be at least 6 characters.');
        if (pwd.next !== pwd.confirm) return alert('New passwords do not match.');
        try {
            setBusy(true);
            await api.post('/change-password', {
                currentPassword: pwd.current,
                newPassword: pwd.next,
            });
            alert('Password updated.');
            setPwd({ current: '', next: '', confirm: '' });
        } catch (err) {
            alert(err.response?.data?.error || 'Failed to change password');
        } finally {
            setBusy(false);
        }
    };

    function fileToDataURL(file) {
        return new Promise((resolve, reject) => {
            const fr = new FileReader();
            fr.onload = () => resolve(fr.result);
            fr.onerror = reject;
            fr.readAsDataURL(file);
        });
    }

    function downscaleDataUrl(dataUrl, max = 512, quality = 0.85) {
        return new Promise((resolve) => {
            const img = new Image();
            img.onload = () => {
                const scale = Math.min(max / img.width, max / img.height, 1);
                const w = Math.round(img.width * scale);
                const h = Math.round(img.height * scale);
                const canvas = document.createElement('canvas');
                canvas.width = w; canvas.height = h;
                const ctx = canvas.getContext('2d');
                ctx.drawImage(img, 0, 0, w, h);
                resolve(canvas.toDataURL('image/jpeg', quality));
            };
            img.src = dataUrl;
        });
    }

    const onSearchSubmit = (e) => {
        e.preventDefault();
        const q = query.trim();
        if (q) console.log('search:', q);
    };

    const saveProfile = async () => {
        try {
            const body = { username: profile.username };
            if (pfp) body.pfp = pfp;
            if (profile.gender) body.gender = profile.gender;
            if (profile.dob) body.dob = profile.dob;

            await api.put('/profile', body);
            navigate(-1);
        } catch (e) {
            alert(e.response?.data?.error || 'Failed to save profile');
        }
    };

    const logout = async () => {
        try {
            await api.post('/logout');
            navigate('/');
        } catch {
            alert('Logout failed');
        }
    };

    return (
        <div className="profileContainer">
            {/*---------Right profile page---------*/}
            <div className="right-profileContainer">
                <h3 style={{ marginTop: 40 }}>个人资料</h3>
                <hr style={{ marginTop: 30 }} />

                <div className="edit-previewContainer">
                    <form className="pillForm" onSubmit={(e) => e.preventDefault()}>
                        <div className="formGrid">
                            <div className="pillField">
                                <input
                                    type="text"
                                    placeholder="Username"
                                    value={profile.username}
                                    onChange={(e) => setProfile((p) => ({ ...p, username: e.target.value }))}
                                />
                            </div>
                            <div className="pillField">
                                <select
                                    value={profile.gender}
                                    onChange={(e) => setProfile((p) => ({ ...p, gender: e.target.value }))}
                                >
                                    <option value="" disabled hidden>Gender</option>
                                    <option value="male">Male</option>
                                    <option value="female">Female</option>
                                    <option value="unknown">Unknown</option>
                                </select>
                            </div>
                            <div className="pillField">
                                <input
                                    type="date"
                                    value={profile.dob}
                                    onChange={(e) => setProfile((p) => ({ ...p, dob: e.target.value }))}
                                />
                            </div>
                            <div className="pillField">
                                <input />
                            </div>
                            <div className="pillField">
                                <input />
                            </div>
                            <div className="pillField">
                                <input />
                            </div>
                            <div className="pillField">
                                <input />
                            </div>

                            <hr className="sectionDivider" />

                            {/*------Change Password------*/}
                            <div className="pillField">
                                <input
                                    type="password"
                                    placeholder="Current password"
                                    value={pwd.current}
                                    onChange={(e) => setPwd((p) => ({ ...p, current: e.target.value }))}
                                />
                            </div>

                            <div className="pillField">
                                <input
                                    type="password"
                                    placeholder="New password"
                                    value={pwd.next}
                                    onChange={(e) => setPwd((p) => ({ ...p, next: e.target.value }))}
                                />
                            </div>

                            <div className="pillField">
                                <input
                                    type="password"
                                    placeholder="Confirm new password"
                                    value={pwd.confirm}
                                    onChange={(e) => setPwd((p) => ({ ...p, confirm: e.target.value }))}
                                />
                            </div>

                            <button
                                type="button"
                                className="pillAction"
                                onClick={changePassword}
                                disabled={busy}
                            >
                                {busy ? 'Updating…' : 'Update password'}
                            </button>

                        </div>
                    </form>

                    {/*---------Preview Card---------*/}
                    <div className="previewContainer">
                        <button
                            className="previewPfp"
                            type="button"
                            onClick={() => fileInputRef.current?.click()}
                            title="Click to upload profile photo"
                        >
                            {pfp ? <img src={pfp} alt="Profile" /> :
                                (profile.username?.[0]?.toUpperCase() || me.email?.[0]?.toUpperCase() || 'U')}
                        </button>
                        <input
                            type="file"
                            accept="image/*"
                            ref={fileInputRef}
                            style={{ display: 'none' }}
                            onChange={async (e) => {
                                const file = e.target.files?.[0];
                                if (!file) return;
                                const dataUrl = await fileToDataURL(file);
                                const small = await downscaleDataUrl(dataUrl, 512, 0.85);
                                if (small.length > 400_000) {
                                    alert('Image still too large after compression.');
                                    return;
                                }
                                setPfp(small);
                            }}
                        />

                        <div className="infoList">
                            <div className="infoRow">
                                <span className="infoKey">Username</span>
                                <span className="infoVal">{profile.username || '—'}</span>
                            </div>
                            <div className="infoRow">
                                <span className="infoKey">Email</span>
                                <span className="infoVal">{me.email || '—'}</span>
                            </div>
                            <div className="infoRow">
                                <span className="infoKey">Gender</span>
                                <span className="infoVal">{profile.gender || '—'}</span>
                            </div>
                            <div className="infoRow">
                                <span className="infoKey">Date of Birth</span>
                                <span className="infoVal">{profile.dob || '—'}</span>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="modal-actions">
                    <button onClick={() => navigate(-1)}>Cancel</button>
                    <button onClick={saveProfile} disabled={!profile.username.trim()}>Save</button>
                    <button onClick={() => api.post('/logout').then(() => navigate('/'))}>
                        Logout
                    </button>
                </div>
            </div>
        </div>
    );
}


